import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Parse markdown-style links in text and render them as React components
 * Supports format: [link text](url)
 * 
 * @param {string} text - Text containing markdown links
 * @param {string} className - Optional CSS class for links
 * @returns {React.ReactNode[]} Array of text and link elements
 */
export const parseMarkdownLinks = (text, className = "text-orange-600 hover:text-orange-700 underline") => {
  if (!text) return null;
  
  // Regex to match [text](url) pattern
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  
  const parts = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;
  
  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    
    const linkText = match[1];
    const url = match[2];
    
    // Check if it's an internal link (starts with /)
    if (url.startsWith('/')) {
      parts.push(
        <Link 
          key={`link-${keyIndex++}`}
          to={url} 
          className={className}
        >
          {linkText}
        </Link>
      );
    } else {
      // External link
      parts.push(
        <a 
          key={`link-${keyIndex++}`}
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          className={className}
        >
          {linkText}
        </a>
      );
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text after last link
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  
  return parts.length > 0 ? parts : text;
};

/**
 * Component that renders text with markdown links
 */
export const TextWithLinks = ({ text, className, linkClassName }) => {
  if (!text) return null;
  
  return (
    <span className={className}>
      {parseMarkdownLinks(text, linkClassName)}
    </span>
  );
};

/**
 * Component that renders a paragraph with markdown links
 */
export const ParagraphWithLinks = ({ text, className = "text-gray-600", linkClassName }) => {
  if (!text) return null;
  
  return (
    <p className={className}>
      {parseMarkdownLinks(text, linkClassName)}
    </p>
  );
};

export default parseMarkdownLinks;
